import {IPriceRange} from 'coreClasses/product/IProduct';
import ProductPriceModel from 'coreClasses/product/Product/ProductPriceModel';
import VariantModel from 'coreClasses/product/VariantModel';

export interface ProductsState {
	[key: string]: Product;
}

export interface Product {
	slugUrl: string;
	brand?: string;
	bundledProducts?: Array<BundledProduct>;
	currency?: string;
	ean?: string;
	fetchDate?: number;
	id: string;
	imageGroups?: Array<ImageGroup>;
	image?: Image;
	inventories?: Array<Inventory>;
	inventory?: Inventory;
	longDescription?: string;
	manufacturerName?: string;
	manufacturerSku?: string;
	master?: Master;
	minOrderQuantity?: number;
	name?: string;
	options?: Array<Option>;
	pageDescription?: string;
	pageKeywords?: string;
	pageTitle?: string;
	price?: number;
	priceMax?: number;
	prices?: {} & {
		[key: string]: any;
	};
	primaryCategoryId?: string;
	productLinks?: Array<ProductLink>;
	productPromotions?: Array<ProductPromotion>;
	recommendations?: Array<Recommendation>;
	setProducts?: Array<Product>;
	shortDescription?: string;
	c_shortDescription?: string;
	stepQuantity?: number;
	type?: ProductType;
	detail: string;
	unit?: string;
	upc?: string;
	validFrom?: any;
	validTo?: any;
	variants?: Array<Variant>;
	fullVariants?: Array<Product>;
	variationAttributes?: Array<VariationAttribute>;
	variationGroups?: Array<VariationGroup>;
	variationValues?: {} & {
		[key: string]: any;
	};
	priceRanges?: Array<IPriceRange>;
	orderable?: boolean;
	c_additionalInfoModuleID?: string;
	c_alternativeImageIndex?: number;
	c_categoryIdForAlternativeImage?: string;
	c_brandCategoryId?: string;
	c_categoryId?: string;
	c_season?: string;
	c_color?: string;
	c_colorDescription?: string;
	c_size?: string;
	c_width?: string;
	c_femaleImage?: boolean;
	c_productVideos?: string;
	c_plpProductVideos?: string;
	c_badges?: string;
	c_imageBadge?: string;
	c_isNewtest?: boolean;
	c_additionalLinks?: string;
	c_pdpDesktopEditorialID?: string;
	c_availableForCustomerGroupA?: string;
	c_availableForCustomerGroupB?: string;
	c_isSalesPriceBookApplied?: boolean;
	c_noReturnCountries?: string;
	c_preorderMessage?: string;
	c_isMadeToOrder?: boolean;
	c_sizeGuide?: string;
	c_extendedMfvc?: string;
	c_hexadecBasicColor?: Array<string>;
	c_productType?: string;
	c_variationGroupsColorGroup?: ТVariationGroupsColorGroup;
	c_variationGroupsHexColor?: Record<string, string>;
	c_variationGroupsColorDescription?: Record<string, string>;
	c_swatchImageGroups?: Record<string, ISwatchImageGroup>;
	c_virtualTryOnEnabled?: boolean;
	c_colorGroup?: string;
	tieredPrices?: TieredPrices[];
	c_productTag: string;
	c_dataLayerDimension34: string;
	c_materialDescription?: string;
	c_fitInformation?: string;
	c_sizeAndFit?: string;
	c_shippingInformation?: string;
	c_returnsAndRefundInformation?: string;
	c_has3dModel: boolean;
	c_virtualMirrorEnabled: boolean;
	c_beautyVirtualTryOnEnabled: boolean;
	c_3dModelConfig: string;
	c_itemMeasurements?: string;
	c_productTabs?: string;
	c_sizeGuideMessage?: string;
	c_banningMessage?: string;
	c_pdpContentAsset?: string;
	c_pdpMessageID?: string;
	c_isFood?: boolean;
	c_isAlcohol?: boolean;
	c_isSearchableIfUnavailable?: boolean;
	c_subName?: string;
	c_familyName?: string;
	c_careInstructions?: string;
	c_useAlternativeNotifyMeCTA?: boolean;
	toBeRefreshed?: boolean;
	ready?: boolean;
	updated: number;
	c_fitInformation2: string;
	c_isEngravable?: boolean;
	c_engraveConfiguration?: any;
	c_showStockThreshold?: number;
	c_bundleKitConfiguration?: string;
	c_washingIcons?: Array<string>;
	c_hideWashingIcons?: boolean;
	c_logisticsProductType?: string;
	c_model?: string;
	offline?: boolean;
	c_persistentUUID?: string;
	c_pdpBackgroundColorPrimary?: string;
	c_pdpBackgroundColorSecondary?: string;
	c_isCustomizable?: boolean;
	c_hasAvailableVariant?: boolean;
	c_isPersonalizable?: boolean;
	c_personalizationPreviewConfiguration: string | TPersonalizationPreviewConfiguration;
	c_personalizationColor: TPersonalizationColor | null;
	c_shadeFinderEnabled?: boolean;
	c_personalizationStyle: 'allUppercase' | 'titleCase' | 'preserveCase';
	getPriceModel: () => ProductPriceModel;
	isVariant: () => boolean;
	getSingleVariant: () => VariantModel;
	getBundleKitConfiguration: () => any;
	c_enableRIS?: boolean;
	c_fitOption?: string;
	c_collarOption?: string;
	c_matchItWithConfig?: string;
	c_thronAssetsIds?: string[];
	c_showModelMFVC?: boolean;
	c_taxMessageID?: string;
}

export type TieredPrices = {
	price: number;
	pricebook: string;
	quantity: number;
};

type BundledProduct = {
	id: string;
	product: Product;
	quantity: number;
};

type ImageGroup = {
	images: Array<Image>;
	variationAttributes?: Array<VariationAttribute>;
	viewType: string;
};

export type Image = {
	alt?: string;
	disBaseLink: string;
	link: string;
	title?: string;
};

export type Inventory = {
	ats?: number;
	backorderable?: boolean;
	id: string;
	inStockDate?: any;
	orderable?: boolean;
	preorderable?: boolean;
	stockLevel?: number;
};

export type Master = {
	masterId: string;
	orderable?: boolean;
	price?: number;
	priceMax?: number;
	prices?: {} & {
		[key: string]: any;
	};
};

type Option = {
	description?: string;
	id: string;
	image?: string;
	name?: string;
	values?: Array<OptionValue>;
};

type OptionValue = {
	default?: boolean;
	id: string;
	name?: string;
	price?: number;
};

type ProductLink = {
	sourceProductId: string;
	sourceProductLink: string;
	targetProductId: string;
	targetProductLink: string;
	type: string;
};

export type ProductPromotion = {
	calloutMsg?: string;
	promotionId: string;
	promotionalPrice?: number;
};

export type Recommendation = {
	calloutMsg?: string;
	image?: Image;
	longDescription?: string;
	name?: string;
	recommendationType: RecommendationType;
	recommendedItemId?: string;
	recommendedItemLink?: string;
	shortDescription?: string;
};

type RecommendationType = {
	displayValue: string;
	value: RecommendationTypes;
};

type ProductType = {
	bundle: boolean;
	item: boolean;
	master: boolean;
	option: boolean;
	set: boolean;
	variant: boolean;
	variationGroup: boolean;
	placeholder: boolean;
};

export type ThreeDimensionalModelConfig = {
	pois: string[];
};

export type BundleKitVariable = {
	name?: string;
	products: Array<string | Record<string, string[]>>;
	restrictions: {
		count: number;
	};
}
export type BundleKitConfig = {
	isSteps?: boolean;
	variable: BundleKitVariable | BundleKitVariable[];
	fixed: {
		products: string[];
	};
};

export interface Variant {
	orderable?: boolean;
	price?: number;
	productId: string;
	variationValues?: {} & {
		[key: string]: any;
	};
}

type VariationAttribute = {
	id: string;
	name?: string;
	values?: Array<VariationAttributeValue>;
};

export type ТVariationGroupsColorGroup = {
	id: string;
	name?: string;
	values?: Array<string>;
};

export interface ISwatchImageGroup {
    imageUrl: string;
    productUrl: string;
    alt: string;
    title: string;
}

type VariationAttributeValue = {
	description?: string;
	image?: Image;
	imageSwatch?: Image;
	name?: string;
	orderable?: boolean;
	value: string;
};

export type VariationGroup = {
	orderable: boolean;
	price: number;
	productId: string;
	variationValues: {} & {
		[key: string]: any;
	};
	tieredPrices?: TieredPrices[];
};

export enum RecommendationTypes {
	RECOMMENDATION = 2,
	OTHER = 3,
	STYLE_IT_WITH = 6,
	CROSS_SELL_FIT = 8,
	PDP_SHOP_THE_LOOK = 9,
	VIRTUAL_TRY_ON = 10,
	CROSS_SELL_COLLAR = 11,
}

export type TPersonalizationPreviewConfiguration = {
	fontSize?: number;
	leftSpacing?: number;
	topSpacing?: number;
	orientation?: TPersonalizationOrientation;
	style?: 'allUppercase' | 'titleCase' | 'preserveCase';
};

export type TPersonalizationColor = 'GOLD' | 'SILVER';
export type TPersonalizationOrientation = 'VERTICAL' | 'HORIZONTAL';
